import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { MotionValue, useMotionValue, useMotionValueEvent } from "motion/react";

export const TAB_ITEM_CLASS = "tabItem";

interface IndicatorBox {
  left: number;
  width: number;
}

const useTabIndicator = (
  tabListRef: RefObject<HTMLElement | null>,
  progress: MotionValue<number>,
  layoutKey: string
) => {
  const x = useMotionValue(0);
  const width = useMotionValue(0);
  const syncRef = useRef(() => {});
  syncRef.current = () => {
    const box = getIndicatorBox(
      getTabElements(tabListRef.current),
      progress.get()
    );
    x.set(box.left);
    width.set(box.width);
  };
  const sync = useCallback(() => syncRef.current(), []);
  useMotionValueEvent(progress, "change", sync);
  useLayoutEffect(sync);
  useEffect(
    () => observeTabs(tabListRef.current, sync),
    [tabListRef, sync, layoutKey]
  );
  return { x, width };
};

export default useTabIndicator;

export const getTabElements = (tabList: HTMLElement | null) =>
  Array.from(
    tabList?.querySelectorAll<HTMLElement>(`.${TAB_ITEM_CLASS}`) ?? []
  );

const getIndicatorBox = (
  tabs: HTMLElement[],
  progress: number
): IndicatorBox => {
  if (!tabs.length) return { left: 0, width: 0 };
  const position = Math.max(0, (tabs.length - 1) * progress);
  const index = Math.min(Math.floor(position), tabs.length - 1);
  const current = tabs[index];
  const next = tabs[Math.min(index + 1, tabs.length - 1)];
  const fraction = position - index;
  return {
    left:
      current.offsetLeft + (next.offsetLeft - current.offsetLeft) * fraction,
    width:
      current.offsetWidth + (next.offsetWidth - current.offsetWidth) * fraction,
  };
};

const observeTabs = (tabList: HTMLElement | null, onResize: () => void) => {
  if (!tabList) return;
  const observer = new ResizeObserver(onResize);
  observer.observe(tabList);
  getTabElements(tabList).forEach((tab) => observer.observe(tab));
  return () => observer.disconnect();
};
